import { Box, Flex, Link, Text } from "@chakra-ui/react";
import _ from "lodash";
import React, { useMemo } from "react";
import { VerificationTag } from "src/components/DataDisplay/VerificationTag";
import { useVisibilityContext } from "src/components/Providers/FormProvider";
import { Glossary } from "src/components/Text/Glossary";
import { Answer, Question } from "src/services/formTemplate";
import * as AF from "src/types/formTemplate";
import * as GQL from "src/types/graphql";

type Props = {
  verificationSummary: GQL.GetVerificationsForAdmin;
  sections: AF.Sections<AF.WithId>;
  form_answers: GQL.FormAnswerFragment[];
  grades_answers: GQL.GradesAnswersFragment[];
  address_answers: GQL.AddressAnswersFragment[];
  custom_question_answers: GQL.CustomQuestionAnswersFragment[];
  custom_question_answer_bank_relationships?: GQL.CustomQuestionAnswerBankRelationshipsFragment[];
  rankedSchoolIds: uuid[];
  previousFormSchoolIds: uuid[];
};

export const VerificationSummary: React.FC<Props> = ({
  verificationSummary,
  sections,
  form_answers,
  grades_answers,
  address_answers,
  custom_question_answers,
  custom_question_answer_bank_relationships,
  rankedSchoolIds,
  previousFormSchoolIds,
}) => {
  const visibilityContext = useVisibilityContext();

  // Get all questions from all sections
  const allQuestions = useMemo(() => {
    return sections.flatMap((section) =>
      section && "questions" in section ? section.questions : []
    );
  }, [sections]);

  // Create answers object
  const answers = useMemo(
    () =>
      Answer.getFormikInitialValues(
        allQuestions,
        form_answers,
        grades_answers,
        address_answers,
        custom_question_answers,
        custom_question_answer_bank_relationships
      ),
    [
      allQuestions,
      form_answers,
      grades_answers,
      address_answers,
      custom_question_answers,
      custom_question_answer_bank_relationships,
    ]
  );

  // Use the same logic as the detail view to determine which verifications should be shown
  const applicableVerifications = useMemo(() => {
    const completeQuestions = Question.getCompleteApplicableQuestions(
      allQuestions,
      answers,
      {
        rankedSchoolIds,
        previousFormSchoolIds,
      },
      visibilityContext
    );

    const verificationGroups = Question.groupByVerifications(completeQuestions);

    // Get the verification IDs that have questions in the detail view
    const visibleVerificationIds = new Set(
      verificationGroups
        .filter((group) => group.verification && group.questions.length > 0)
        .map((group) => group.verification!.id)
    );

    // Filter to only include verifications that are visible in the detail view
    return verificationSummary.form_verification.filter((verification) =>
      visibleVerificationIds.has(verification.id)
    );
  }, [
    allQuestions,
    answers,
    rankedSchoolIds,
    previousFormSchoolIds,
    visibilityContext,
    verificationSummary.form_verification,
  ]);

  if (applicableVerifications.length === 0) {
    return null;
  }

  const handleScrollToVerification = (verificationId: string) => {
    const element = document.getElementById(`verification-${verificationId}`);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <Flex
      borderRadius="md"
      border="1px solid"
      borderColor="gray.300"
      direction="column"
      padding={3}
      gap={3}
      minWidth="15rem"
    >
      <Text fontWeight="600">
        <Glossary>Verifications</Glossary>
      </Text>
      {_.sortBy(
        applicableVerifications,
        (verifications) => verifications.label
      ).map((verification, index) => {
        const result = verificationSummary.form_verification_result.find(
          (result) => result.form_verification_id === verification.id
        );
        return (
          <Flex key={index} alignItems="flex-start" gap={2}>
            <Link
              fontSize=".875rem"
              color="primary.500"
              cursor="pointer"
              onClick={() => handleScrollToVerification(verification.id)}
              flex="1"
              wordBreak="break-word"
              lineHeight="1.4"
            >
              {verification.label}
            </Link>
            <Box flexShrink={0}>
              <VerificationTag
                status={
                  result?.verification_status ??
                  GQL.verification_status_enum.Pending
                }
                label={verification.label}
                showText="status"
              />
            </Box>
          </Flex>
        );
      })}
    </Flex>
  );
};
